import { EarningsCommissionTier, EarningsTierSegmentState } from "@/types";

// Each segment is twice as wide as the next so the earliest tier reads largest.
const getSegmentFlexGrowValues = (segmentCount: number): number[] =>
  Array.from({ length: segmentCount }, (_, index) =>
    Math.pow(2, segmentCount - 1 - index),
  );

// Revenue range a tier covers; end is null on the uncapped last tier.
const getTierRange = (
  tiers: EarningsCommissionTier[],
  index: number,
): { endThresholdDollars: number | null; startThresholdDollars: number } => {
  const startThresholdDollars =
    index === 0 ? 0 : tiers[index]?.thresholdDollars || 0;
  const endThresholdDollars =
    index >= tiers.length - 1
      ? null
      : tiers[index + 1]?.thresholdDollars || null;

  return { endThresholdDollars, startThresholdDollars };
};

// Blue → green ramp across the tier segments.
const tickColorAtPercent = (percent: number): string => {
  const ratio = Math.min(100, Math.max(0, percent)) / 100;
  const red = Math.round(37 + (34 - 37) * ratio);
  const green = Math.round(99 + (197 - 99) * ratio);
  const blue = Math.round(235 + (94 - 235) * ratio);
  return `rgb(${red.toString()}, ${green.toString()}, ${blue.toString()})`;
};

export const earningsProgress = {
  // Marginal commission earned on the revenue between two YTD totals, blended
  // across whichever tiers that span crosses. Mirrors the guac engine's math.
  commissionBetween: (
    startRevenueDollars: number,
    endRevenueDollars: number,
    tiers: EarningsCommissionTier[],
  ): number =>
    tiers.reduce((total, tier, index) => {
      const { endThresholdDollars, startThresholdDollars } = getTierRange(
        tiers,
        index,
      );
      const overlapDollars =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        Math.min(endRevenueDollars, endThresholdDollars || Infinity) -
        Math.max(startRevenueDollars, startThresholdDollars);
      if (overlapDollars <= 0) return total;
      return total + overlapDollars * (tier.commissionPercent / 100);
    }, 0),
  // Color for a segment's bar/label, sampled from the blue → green ramp.
  tickColorAtSegmentIndex: (index: number, segmentCount: number): string => {
    if (segmentCount <= 1) {
      return tickColorAtPercent(0);
    }
    return tickColorAtPercent((index / (segmentCount - 1)) * 100);
  },
  // flex-grow for a segment so widths follow the 2x taper.
  getSegmentFlexGrow: (segmentCount: number, index: number): number => {
    return getSegmentFlexGrowValues(segmentCount)[index] || 1;
  },
  // Index of the tier the revenue currently sits in.
  getCurrentTierIndex: (
    totalRevenueDollars: number,
    tiers: EarningsCommissionTier[],
  ): number => {
    for (let index = tiers.length - 1; index >= 0; index--) {
      const { startThresholdDollars } = getTierRange(tiers, index);
      if (totalRevenueDollars >= startThresholdDollars) {
        return index;
      }
    }
    return 0;
  },
  // Gap to the next tier for the hover tooltip; null once on the last tier.
  getDollarsToNextTier: (
    totalRevenueDollars: number,
    tiers: EarningsCommissionTier[],
  ): { dollarsRemaining: number; nextCommissionPercent: number } | null => {
    const currentTierIndex = earningsProgress.getCurrentTierIndex(
      totalRevenueDollars,
      tiers,
    );
    const { endThresholdDollars } = getTierRange(tiers, currentTierIndex);

    // endThresholdDollars is null only on the last tier, so nextTier exists past this guard.
    if (endThresholdDollars === null) {
      return null;
    }

    const nextTier = tiers[currentTierIndex + 1];

    return {
      dollarsRemaining: endThresholdDollars - totalRevenueDollars,
      nextCommissionPercent: nextTier.commissionPercent,
    };
  },
  // Whether a segment renders full (complete), partially filled (current), or dimmed (upcoming).
  getTierSegmentState: (
    totalRevenueDollars: number,
    tiers: EarningsCommissionTier[],
    index: number,
  ): { fillRatio: number; state: EarningsTierSegmentState } => {
    const { endThresholdDollars, startThresholdDollars } = getTierRange(
      tiers,
      index,
    );

    if (
      endThresholdDollars !== null &&
      totalRevenueDollars >= endThresholdDollars
    ) {
      return { state: EarningsTierSegmentState.COMPLETE, fillRatio: 1 };
    }

    if (totalRevenueDollars < startThresholdDollars) {
      return { state: EarningsTierSegmentState.UPCOMING, fillRatio: 0 };
    }

    if (endThresholdDollars === null) {
      return { state: EarningsTierSegmentState.CURRENT, fillRatio: 1 };
    }

    const span = endThresholdDollars - startThresholdDollars;
    const fillRatio =
      span > 0 ? (totalRevenueDollars - startThresholdDollars) / span : 1;

    return {
      state: EarningsTierSegmentState.CURRENT,
      fillRatio: Math.min(1, Math.max(0, fillRatio)),
    };
  },
};
